'use client';

import React, { useId, useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void | Promise<void>;
  loading?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, loading = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseId = useId();
  const emailId = `${baseId}-email`;
  const passwordId = `${baseId}-password`;
  const emailErrorId = `${emailId}-error`;
  const passwordErrorId = `${passwordId}-error`;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  const isLoading = loading || isSubmitting;

  const onFormSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await onSubmit(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'We could not sign you in. Please try again.'
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="flex flex-col gap-5"
      aria-busy={isLoading}
    >
      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-3 rounded-lg border border-danger/20 bg-danger/5 p-3 text-sm text-danger"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={emailId}
          className="text-sm font-medium text-foreground"
        >
          Email address
        </label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          {...register('email')}
          placeholder="you@example.com"
          variant={errors.email ? 'error' : 'default'}
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? emailErrorId : undefined}
        />
        {errors.email && (
          <p id={emailErrorId} className="text-xs text-danger" aria-live="polite">
            {String(errors.email?.message)}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={passwordId}
          className="text-sm font-medium text-foreground"
        >
          Password
        </label>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...register('password')}
            placeholder="••••••••"
            variant={errors.password ? 'error' : 'default'}
            aria-invalid={errors.password ? 'true' : 'false'}
            aria-describedby={errors.password ? passwordErrorId : undefined}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 inline-flex -translate-y-1/2 items-center justify-center p-3.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-md focus-visible:text-foreground"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="size-4" aria-hidden="true" />
            ) : (
              <Eye className="size-4" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.password && (
          <p id={passwordErrorId} className="text-xs text-danger" aria-live="polite">
            {String(errors.password?.message)}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('rememberMe')}
            className="min-h-11 min-w-11 rounded border-border bg-background p-3.5 text-primary focus:ring-primary focus:ring-offset-0"
          />
          <span className="text-sm text-ash">Remember me</span>
        </label>
          <Link
            href="/forgot-password"
            className="inline-flex min-h-11 items-center text-sm font-medium text-primary transition-colors hover:text-primary-light hover:underline focus-visible:rounded-md focus-visible:text-primary-light"
          >
          Forgot password?
        </Link>
      </div>

          <Button
            type="submit"
            disabled={isLoading}
            loading={isLoading}
            className="w-full min-h-11"
          >
        {!isLoading && <LogIn className="size-4" aria-hidden="true" />}
        {isLoading ? 'Signing in...' : 'Sign in'}
      </Button>

      <p className="text-center text-sm text-ash">
        Don&apos;t have an account?{' '}
          <Link
            href="/register"
            className="inline-flex min-h-11 items-center font-semibold text-primary transition-colors hover:text-primary-light hover:underline focus-visible:rounded-md focus-visible:text-primary-light"
          >
          Create one
        </Link>
      </p>
    </form>
  );
};

export default LoginForm;
